import { post, get } from './axios.config';
import localStorageService from '../storage/localStorage.service';

/**
 * Serviço de autenticação
 */
const authService = {
  /**
   * Realiza login (admin ou tenant)
   * @param {Object} credentials - { email, password, type }
   * type: 'admin' | 'tenant' (padrão: 'tenant')
   * @returns {Promise<Object>} - Dados do usuário e token
   */
  login: async (credentials) => {
    try {
      const { email, password, type = 'tenant' } = credentials;
      
      console.log('🔐 Tentando login:', { email, type });
      
      // Define endpoint baseado no tipo de login
      const endpoint = type === 'admin' 
        ? '/auth/admin/login' 
        : '/auth/tenant/login';
      
      console.log('📡 Endpoint:', endpoint);
      
      const response = await post(endpoint, { email, password });
      
      console.log('✅ Resposta da API:', response.data);
      
      let userData;
      
      if (type === 'admin') {
        // Resposta do admin: { token, admin: { id, name, email } }
        const { token, admin } = response.data;
        
        userData = {
          id: admin.id,
          name: admin.name,
          email: admin.email,
          role: 'admin',
          type: 'admin',
        };
        
        localStorageService.setAuthToken(token);
        localStorageService.setUserData(userData);
        
        console.log('💾 Admin salvo:', userData);
        
        return { token, user: userData };
        
      } else {
        // Resposta do tenant: { token, tenant: { id, name, email }, expiresIn }
        const { token, tenant, expiresIn } = response.data;
        
        userData = {
          id: tenant.id,
          name: tenant.name,
          email: tenant.email,
          role: 'client',
          type: 'tenant',
          tenantId: tenant.id,
        };
        
        localStorageService.setAuthToken(token);
        localStorageService.setUserData(userData);
        
        console.log('💾 Tenant salvo:', userData);
        
        return { token, user: userData, expiresIn };
      }
      
    } catch (error) {
      console.error('❌ Erro no login:', error);
      throw error.response?.data || { message: 'Erro ao fazer login' };
    }
  },

  /**
   * Realiza logout
   * @returns {Promise<void>}
   */
  logout: async () => {
    try {
      // API não tem endpoint de logout, apenas limpa dados locais
      localStorageService.clearAuth();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      localStorageService.clearAuth();
    }
  },

  /**
   * Verifica se token ainda é válido
   * Como a API não tem endpoint de validação, verifica localmente
   * @returns {Promise<boolean>}
   */
  validateToken: async () => {
    try {
      const token = localStorageService.getAuthToken();
      
      if (!token) {
        return false;
      }

      // Decodifica o token JWT para verificar expiração
      const payload = decodeToken(token);
      
      if (!payload || !payload.exp) {
        return false;
      }

      // Verifica se não expirou
      const now = Date.now() / 1000;
      return payload.exp > now;
    } catch (error) {
      return false;
    }
  },

  /**
   * Obtém dados do usuário autenticado
   * Como a API não tem endpoint /me, retorna dados do localStorage
   * @returns {Promise<Object>}
   */
  getCurrentUser: async () => {
    try {
      const userData = localStorageService.getUserData();
      
      if (!userData) {
        throw new Error('Usuário não encontrado');
      }
      
      return userData;
    } catch (error) {
      throw error.response?.data || { message: 'Erro ao buscar dados do usuário' };
    }
  },

  /**
   * Verifica se usuário está autenticado localmente
   * @returns {boolean}
   */
  isAuthenticated: () => {
    return localStorageService.isAuthenticated();
  },

  /**
   * Obtém token armazenado
   * @returns {string|null}
   */
  getToken: () => {
    return localStorageService.getAuthToken();
  },

  /**
   * Obtém dados do usuário armazenados
   * @returns {Object|null}
   */
  getUserData: () => {
    return localStorageService.getUserData();
  },

  /**
   * Verifica se usuário é admin
   * @returns {boolean}
   */
  isAdmin: () => {
    const userData = localStorageService.getUserData();
    return userData?.type === 'admin';
  },

  /**
   * Verifica se usuário é tenant
   * @returns {boolean}
   */
  isTenant: () => {
    const userData = localStorageService.getUserData();
    return userData?.type === 'tenant';
  },
};

/**
 * Decodifica token JWT
 * @param {string} token 
 * @returns {Object|null}
 */
function decodeToken(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Erro ao decodificar token:', error);
    return null;
  }
}

export default authService;